import React from "react";
import { Lock, Check, Shapes } from "lucide-react";
import { STYLE_PACKS, recommendPacksFor, DEFAULT_PACKS } from "../lib/stylePacks";

/**
 * PackPicker — pre-generation Style Pack chooser.
 *
 * Free users see 4 packs recommended for the inferred emotion lane. They
 * can pick any of those 4. Tapping outside that set (premium-only packs)
 * fires onPremiumLocked.
 *
 * Premium users see all 6 packs and can pick freely.
 *
 * Each chip now ships with a tiny LIVE PREVIEW thumbnail rendering the
 * pack's first variant — so users see what they're picking before they
 * generate, instead of guessing from the label alone.
 *
 * value === null means "auto" (backend picks from lane). Tapping the
 * same chip again deselects → returns to auto. Cards rendered before
 * Style Pack v2 launched (with legacy packs like "polaroid"/"brutalist")
 * keep working unchanged because the legacy packTreatment fallback in
 * MoodCard.jsx still handles them.
 */
export default function PackPicker({ value, onChange, lane, isPremium, onPremiumLocked }) {
    const recommended = recommendPacksFor(lane);
    const allPacks = isPremium ? DEFAULT_PACKS : recommended;
    const seen = new Set();
    const visiblePackIds = [...allPacks, ...DEFAULT_PACKS].filter((id) => {
        if (seen.has(id)) return false;
        seen.add(id);
        return true;
    });

    const handlePick = (packId) => {
        const isFree = recommended.includes(packId);
        if (!isFree && !isPremium) {
            if (onPremiumLocked) onPremiumLocked(packId);
            return;
        }
        if (value === packId) {
            onChange(null);  // toggle off → back to auto
        } else {
            onChange(packId);
        }
    };

    return (
        <div className="w-full" data-testid="pack-picker">
            <div className="flex items-center justify-between mb-2 px-1">
                <div className="flex items-center gap-2">
                    <Shapes className="w-3.5 h-3.5 text-app-3" strokeWidth={1.5} />
                    <span className="font-sans text-[10px] uppercase tracking-[0.35em] text-app-3">
                        shape · {value ? STYLE_PACKS[value]?.label?.toLowerCase() || "auto" : "auto"}
                    </span>
                </div>
                {value && (
                    <button
                        type="button"
                        onClick={() => onChange(null)}
                        className="font-sans text-[9px] uppercase tracking-[0.32em] text-app-3 hover:text-app transition-colors"
                        data-testid="pack-picker-clear"
                    >
                        clear
                    </button>
                )}
            </div>
            <div className="flex flex-wrap gap-1.5" data-testid="pack-picker-chips">
                {visiblePackIds.map((id) => {
                    const pack = STYLE_PACKS[id];
                    if (!pack) return null;
                    const isSelected = value === id;
                    const isFreeAccessible = recommended.includes(id);
                    const isLocked = !isFreeAccessible && !isPremium;
                    // Live preview = first variant's background painted into
                    // a tiny thumbnail next to the chip text. Lets users see
                    // what they're picking before generating.
                    const preview = pack.variants[0];
                    return (
                        <button
                            key={id}
                            type="button"
                            onClick={() => handlePick(id)}
                            data-testid={`pack-chip-${id}`}
                            data-locked={isLocked ? "true" : "false"}
                            className={`btn-press inline-flex items-center gap-2 pl-1.5 pr-3 py-1 rounded-full border font-sans text-[10px] uppercase tracking-[0.22em] transition-colors ${
                                isSelected
                                    ? "border-amber-300/55 bg-amber-400/10 text-amber-100"
                                    : isLocked
                                        ? "border-app/15 bg-surface-1 text-app-3 opacity-65"
                                        : "border-app/20 bg-surface-1 text-app-2 hover:text-app hover:border-app/45"
                            }`}
                        >
                            <span
                                aria-hidden="true"
                                data-testid={`pack-chip-preview-${id}`}
                                className="block w-[18px] h-[24px] rounded-[3px] flex-shrink-0 border border-black/20"
                                style={{
                                    background: preview.background,
                                    backgroundSize: "cover",
                                    backgroundPosition: "center",
                                    boxShadow: "inset 0 0 8px rgba(0,0,0,0.15)",
                                }}
                            />
                            {isSelected && <Check className="w-2.5 h-2.5" strokeWidth={2.4} />}
                            <span>{pack.label.toLowerCase()}</span>
                            {isLocked && <Lock className="w-2.5 h-2.5 opacity-75" strokeWidth={1.6} />}
                        </button>
                    );
                })}
            </div>
            {!isPremium && (
                <p className="mt-2 px-1 font-sans text-[9px] uppercase tracking-[0.3em] text-app-3 opacity-70">
                    {recommended.length} packs free for this mood · <span className="text-amber-200/80">unlock all 6 with premium</span>
                </p>
            )}
        </div>
    );
}
